import React, { FC, PropsWithChildren } from "react";
import styled, { css } from "styled-components";

interface IButton extends IButtonStyled {
  type?: "button" | "submit" | "reset";
  onClick?: (e?: MouseEvent) => void;
}

interface IButtonStyled {
  isDisabled?: boolean;
  isBlock?: boolean;
  buttonType?: string;
  isOutlined?: boolean;
  borderColor?: string;
  textColor?: string;
  backgroundColor?: string;
  size?: "sm" | "l";
  withShadow?: boolean;
  fullWidth?: boolean;
}

const StyledButton = styled.button<IButtonStyled>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ isBlock }) => (isBlock && "center") || "center"};
  transition: 0.4s;
  width: ${({ isBlock }) => (isBlock ? "100%" : "200px")};
  border-radius: 5px !important;
  font-weight: 500;
  font-size: 16px;
  background-color: var(--${({ backgroundColor }) => backgroundColor});
  border: 1px solid var(--${({ borderColor }) => borderColor});

  box-shadow: ${({ withShadow }) =>
    withShadow
      ? "0px 16px 15px rgba(0, 0, 0, 25% )"
      : "rgb(0 0 0 / 15%) 0px 0px 40px"};
  padding: ${({ size }) => (size === "l" ? "12px 21px" : "4px 9px")};
  line-height: 1.5em;
  color: var(--${({ textColor }) => textColor});

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      @media (max-width: 600px) {
        width: 100%;
      }
    `}

  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-disabled);
  }

  &:not(:disabled):hover {
    color: var(--${({ textColor }) => textColor});
  }

  &:not(:disabled):focus {
    color: var(--${({ textColor }) => textColor});
    box-shadow: var(--box-shadow-focus);
  }

  ${({ buttonType }) =>
    buttonType === "secondary" &&
    `
  border-color: black;
  background-color: white;
  color: black;
  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-disabled);
  }

  &:not(:disabled):hover {
    color: black;
    background-color: white;
    border-color: black;
  }

  &:not(:disabled):focus {
    color: black;
    background-color: white;
    border-color: black;
  }
`}
`;

const StyledText = styled.span`
  color: inherit;
  pointer-events: none;
`;

const Button: FC<PropsWithChildren<IButton>> = (props): JSX.Element => {
  const {
    children,
    isDisabled,
    isOutlined = false,
    type = "button",
    isBlock,
    borderColor = "button-border",
    backgroundColor = "button",
    textColor = "color-white",
    buttonType = "primary",
    size = "l",
    onClick,
    withShadow,
    fullWidth
  } = props;

  const handleButtonClick = () => {
    if (onClick && !isDisabled) {
      onClick();
    }
  };

  return (
    <StyledButton
      size={size}
      isBlock={isBlock}
      type={type}
      disabled={isDisabled}
      isOutlined={isOutlined}
      borderColor={borderColor}
      textColor={textColor}
      backgroundColor={backgroundColor}
      onClick={handleButtonClick}
      buttonType={buttonType}
      withShadow={withShadow}
      fullWidth={fullWidth}
    >
      <StyledText>{children}</StyledText>
    </StyledButton>
  );
};

export default Button;
